import PropTypes from 'prop-types';

import {CoverSwatchGrid} from '@amblique/king-living/app/components/cover-swatch-grid';
import {CustomizeStepsInline} from '@amblique/king-living/app/components/complex-customize/customize-steps-inline';

/**
 * KINDW-1124 — Composition slot for Simple PDPs of type CONFIGURABLE_MASTER
 * (TSD §"Configurable master products" / FSD §SECTION-PDP-SIMPLE-08).
 *
 * A Configurable Master is a variation-base master with required recommendation
 * containers but **no linked products** ("Choose Your Set"). It is the natural
 * superset of:
 *   - Freeform (KINDW-1121) — provides the cover swatch grid + OOS Notify Me CTA.
 *     Cover auto-preselect is Freeform-only (FSD §5.1); Configurable Master leaves cover
 *     unselected on load (KINDW-1595 / FSD §SECTION-PDP-SIMPLE-08).
 *   - Multi-step (KINDW-1119) — provides the inline customize steps chain +
 *     `<ComplexAddToCartButton>` payload (parent cover variant + child
 *     recommendation line items).
 *
 * This slot owns the CM-specific composition (cover grid + inline steps in the
 * right column body) so the `<ProductView>` render tree reads as a single
 * branch rather than two co-located conditionals. It introduces **no new
 * behaviour** of its own; all data flow remains in `<ProductView>`:
 *   - Cover selection commits via `onCoverSelect` → `committedSelection.cover`
 *     (folded into customize selections by `<ProductView>` so the gallery
 *     reacts to cover swaps — see comment block at `<ProductView>` lines
 *     843‑848).
 *   - Inline gallery / "See Details" callbacks bubble up untouched.
 *
 * The mobile "See Details" overlay (`<ReviewSection asOverlay>`) is shared
 * with KINDW-1118 / KINDW-1119 / KINDW-1123 and stays in `<ProductView>` so a
 * single instance covers all simple PDP types.
 *
 * @see ../../doc/task/PDP-simple/KINDW-1124/KINDW-1124.md
 */
export function ConfigurableMasterView({
  coverVariants,
  coverMasterMinPrice,
  currency,
  onCoverMoreClick,
  onCoverSelect,
  onViewDetails,
  product,
  selectedCoverId,
}) {
  return (
    <>
      {coverVariants?.length > 0 && (
        <CoverSwatchGrid
          currency={currency}
          masterMinPrice={coverMasterMinPrice}
          selectedCoverId={selectedCoverId}
          variants={coverVariants}
          onMoreClick={onCoverMoreClick}
          onSelect={onCoverSelect}
          onViewDetails={onViewDetails}
        />
      )}

      <CustomizeStepsInline />
    </>
  );
}

ConfigurableMasterView.displayName = 'ConfigurableMasterView';

ConfigurableMasterView.propTypes = {
  coverVariants: PropTypes.array,
  coverMasterMinPrice: PropTypes.number,
  currency: PropTypes.string,
  onCoverMoreClick: PropTypes.func,
  onCoverSelect: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  product: PropTypes.object.isRequired,
  selectedCoverId: PropTypes.string,
};

export default ConfigurableMasterView;
